import type { Document } from '@/types/document'

/** GPT-only prefix stored on older CSV uploads before raw_ocr_text was split. */
export const CSV_EXTRACTION_PROMPT_PREFIX =
  'The following content is from an uploaded CSV (comma- or tab-separated). Infer vendor, dates, reference, totals, and line items from the columns and rows. Map data rows to bill line items with descriptions and positive amounts; respect negative amounts for credits or discounts.'

export function uploadFilenameFromSourceId(sourceId: string): string {
  const parts = sourceId.split(':', 2)
  return parts.length >= 3 ? (parts[2] ?? sourceId) : sourceId
}

export function isCsvUploadDocument(
  doc: Pick<Document, 'source_type' | 'source_id' | 'pdf_storage_path'>
): boolean {
  if (doc.source_type !== 'upload' || doc.pdf_storage_path) return false
  return uploadFilenameFromSourceId(doc.source_id).toLowerCase().endsWith('.csv')
}

export function stripCsvExtractionPrompt(text: string): string {
  const trimmed = text.trimStart()
  if (!trimmed.startsWith(CSV_EXTRACTION_PROMPT_PREFIX)) return text
  const rest = trimmed.slice(CSV_EXTRACTION_PROMPT_PREFIX.length).replace(/^\s+/, '')
  return rest
}

export interface ParsedDelimitedTable {
  headers: string[]
  rows: string[][]
}

function detectDelimiter(line: string): ',' | '\t' {
  let commaCount = 0
  let tabCount = 0
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        i++
        continue
      }
      inQuotes = !inQuotes
      continue
    }
    if (!inQuotes) {
      if (ch === ',') commaCount++
      if (ch === '\t') tabCount++
    }
  }
  return tabCount > commaCount ? '\t' : ','
}

function parseDelimitedRow(line: string, delimiter: ',' | '\t'): string[] {
  const fields: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        current += ch
      }
      continue
    }
    if (ch === '"') {
      inQuotes = true
      continue
    }
    if (ch === delimiter) {
      fields.push(current.trim())
      current = ''
      continue
    }
    current += ch
  }

  fields.push(current.trim())
  return fields
}

/** Parse comma- or tab-separated text into a table; returns null when parsing fails. */
export function parseDelimitedTable(text: string): ParsedDelimitedTable | null {
  const body = stripCsvExtractionPrompt(text).replace(/^\uFEFF/, '').trim()
  if (!body) return null

  const lines = body.split(/\r?\n/).filter((line) => line.trim().length > 0)
  if (lines.length === 0) return null

  const firstLine = lines[0]!
  const delimiter = detectDelimiter(firstLine)
  const parsedLines = lines.map((line) => parseDelimitedRow(line, delimiter))
  const columnCount = Math.max(...parsedLines.map((row) => row.length))
  if (columnCount === 0) return null

  const padRow = (row: string[]) => {
    const padded = [...row]
    while (padded.length < columnCount) padded.push('')
    return padded.slice(0, columnCount)
  }

  const first = parsedLines[0]
  if (!first) return null
  const rest = parsedLines.slice(1)
  const firstPadded = padRow(first)
  const looksLikeHeader =
    rest.length > 0 &&
    firstPadded.some((cell) => cell.length > 0) &&
    firstPadded.filter((cell) => cell.length > 0).every((cell) => {
      const normalized = cell.trim()
      if (!normalized) return true
      const asNumber = Number(normalized.replace(/[$,]/g, ''))
      return Number.isNaN(asNumber)
    })

  if (looksLikeHeader) {
    const headers = firstPadded.map((cell, idx) => cell || `Column ${idx + 1}`)
    return { headers, rows: rest.map(padRow) }
  }

  const headers = firstPadded.map((_, idx) => `Column ${idx + 1}`)
  return { headers, rows: parsedLines.map(padRow) }
}
